import { useEffect, useMemo } from "react";
import { SectionCard } from "@/components/common/SectionCard";
import { computeOfferingReadiness } from "./serviceSetupReadiness";
import { ServiceSetupReadinessBadge } from "./ServiceSetupReadinessBadge";
import { ServiceSetupReadinessChecklist } from "./ServiceSetupReadinessChecklist";
import { ServiceDefinitionResourcesSection } from "./ServiceDefinitionResourcesSection";
import { ServiceOfferingDocumentsSection } from "./ServiceOfferingDocumentsSection";
import { ServiceSetupAdvancedSection } from "./ServiceSetupAdvancedSection";
import { ServiceSetupBasicsSection } from "./ServiceSetupBasicsSection";
import { ServiceSetupSalesSection } from "./ServiceSetupSalesSection";
import { ghostBtnClass } from "./serviceShared";
import type { useServiceOfferingsCatalog } from "./useServiceOfferingsCatalog";
import type { useServiceDefinitionsPanel } from "./useServiceDefinitionsPanel";
import { useServiceOfferingDocSummaries } from "./useServiceOfferingDocSummaries";

export function ServiceSetupDetailView(props: {
  offeringId: string;
  catalog: ReturnType<typeof useServiceOfferingsCatalog>;
  definitions: ReturnType<typeof useServiceDefinitionsPanel>;
  navTick: number;
  onBack: () => void;
  onRefresh: () => void;
}) {
  const { offeringId, catalog, definitions, navTick, onBack, onRefresh } = props;

  const offering = useMemo(() => {
    return (
      catalog.offerings.find((o) => o.id === offeringId) ??
      definitions.offerings.find((o) => o.id === offeringId) ??
      null
    );
  }, [catalog.offerings, definitions.offerings, offeringId]);

  useEffect(() => {
    definitions.openOfferingScope(offeringId);
  }, [definitions, offeringId]);

  useEffect(() => {
    if (offering) catalog.loadOfferingDraft(offering);
  }, [catalog, offering]);

  const docSummaries = useServiceOfferingDocSummaries([offeringId], Boolean(offering), navTick);
  const docSummary = docSummaries[offeringId];

  if (!offering) {
    return (
      <div className="space-y-3">
        <button type="button" className={ghostBtnClass} onClick={onBack}>
          ← All services
        </button>
        <p className="text-sm text-amber-800 dark:text-amber-200">Service not found or inactive.</p>
      </div>
    );
  }

  const readiness = computeOfferingReadiness({
    offering,
    rigOfferingIds: definitions.rigOfferingIds,
    techOfferingIds: definitions.techOfferingIds,
    docSummary,
  });

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3 justify-between">
        <div className="flex flex-wrap items-center gap-2 min-w-0">
          <button type="button" className={ghostBtnClass} onClick={onBack}>
            ← All services
          </button>
          <div className="min-w-0">
            <h3 className="text-base font-semibold text-gray-900 dark:text-zinc-100">{offering.label}</h3>
            <p className="text-xs text-gray-500 dark:text-zinc-400">{offering.slug}</p>
          </div>
        </div>
        <ServiceSetupReadinessBadge readiness={readiness} />
      </div>

      <SectionCard title="Readiness">
        <ServiceSetupReadinessChecklist readiness={readiness} />
      </SectionCard>

      <ServiceSetupBasicsSection catalog={catalog} onSaved={onRefresh} />
      <ServiceDefinitionResourcesSection
        m={definitions}
        title="Crew & equipment"
        description="Rigs and technicians linked to this service. New entries here are auto-associated."
      />
      <ServiceSetupSalesSection m={definitions} />
      <ServiceOfferingDocumentsSection
        offeringId={offering.id}
        offeringLabel={offering.label}
        presentation="card"
      />
      <ServiceSetupAdvancedSection
        offering={offering}
        catalog={catalog}
        definitions={definitions}
        onSaved={onRefresh}
      />
    </div>
  );
}
